import React, { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import { FallbackProps } from "react-error-boundary";
import { useTranslation } from "react-i18next";

import { getAssetsPath } from "../../utils/assets.utils";
import Button, { ButtonColor } from "../Button";
import { useNavigate, useInRouterContext } from "react-router-dom";

import {
  StyledErrorFallbackWrapper,
  StyledErrorTitle,
  StyledErrorSubTitleContainer,
} from "./Error.styles";
import SanitizedTypography from "../Typography/SanitizedTypography.component";
import useSystemCheckStore from "@/stores/systemCheck.store";

interface ErrorFallbackProps extends FallbackProps {
  error: Error | null;
}

declare const __APP_VERSION__: string;

function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const errorCode = (error as any).code || (error as any).status || "500";
  const inRouter = useInRouterContext();
  const navigate = inRouter ? useNavigate() : null;

  const { t } = useTranslation();
  const isVersionMatched = useSystemCheckStore(
    (state) => state.isVersionMatched
  );
  const setIsVersionMatched = useSystemCheckStore(
    (state) => state.setIsVersionMatched
  );

  const defaultMessageKey = error?.message || "error.fallback.default.message";
  const [translatedMessage, setTranslatedMessage] = useState(
    t(defaultMessageKey)
  );

  const handleClick = () => {
    if (!isVersionMatched) {
      window.location.href = `${location.pathname}?version=${__APP_VERSION__}`;
    } else if (navigate) {
      navigate("/");
    }
  };

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch("/version.json", {
          cache: "no-cache",
        });
        const data = await res.json();
        if (data.version !== __APP_VERSION__ && isVersionMatched) {
          setIsVersionMatched(false);
        }
      } catch (err) {
        console.error("Version check failed", err);
      }
    };
    checkVersion();
  }, [isVersionMatched, setIsVersionMatched]);

  useEffect(() => {
    const updatedKey = !isVersionMatched
      ? "error.fallback.versionMismatch"
      : defaultMessageKey;
    setTranslatedMessage(t(updatedKey));
  }, [isVersionMatched, t, defaultMessageKey]);

  return (
    <StyledErrorFallbackWrapper container>
      <Grid
        sx={{
          position: "absolute",
          top: "1rem",
          left: "1rem",
        }}
      >
        <Grid container sx={{ width: "100%" }} justifyContent={"left"}>
          <Grid>
            <img alt="logo" src={getAssetsPath("images/logo.svg")} />
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <img alt="" src={getAssetsPath(`icons/${errorCode}.svg`)} />
      </Grid>
      <Grid>
        <StyledErrorTitle>
          {translatedMessage
            .split("|")
            .map((errorMessage: any, index: number) => {
              return (
                <React.Fragment key={index}>{t(errorMessage)}</React.Fragment>
              );
            })}
        </StyledErrorTitle>
      </Grid>
      <StyledErrorSubTitleContainer>
        <SanitizedTypography
          dangerouslySetInnerHTML={{
            __html: t("error.fallback.default.subTitle"),
          }}
        />
      </StyledErrorSubTitleContainer>
      <Grid>
        {navigate ? (
          <Button color={ButtonColor.primary} onClick={handleClick}>
            {!isVersionMatched
              ? t("error.fallback.refresh.button.label")
              : t("error.fallback.exit.button.label")}
          </Button>
        ) : (
          <>
            {error?.message ? (
              <>
                {error.stack && (
                  <pre
                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {error.stack}
                  </pre>
                )}
              </>
            ) : (
              t("error.fallback.no.message")
            )}
          </>
        )}
      </Grid>
    </StyledErrorFallbackWrapper>
  );
}

export default ErrorFallback;
